import { createEventHook } from '@vueuse/core';
import { readonly, ref } from 'vue';

export type LoadedEvent = 'loaded' | 'loadedFirstTime';

export function useLoading<T extends (...args: any[]) => Promise<any>>(
  call: T,
  initialLoading = false,
) {
  const loading = ref(initialLoading);

  const loadingFirstTime = ref(initialLoading);

  const loadedHook = createEventHook<LoadedEvent>();

  let numCallsPending: number = 0;

  let isFirstTime = true;

  const action = async (
    ...args: Parameters<typeof call>
  ): Promise<Awaited<ReturnType<typeof call>>> => {
    loadingFirstTime.value = isFirstTime;

    loading.value = true;

    numCallsPending = numCallsPending + 1;

    const result = await call(...args).finally(() => {
      numCallsPending = numCallsPending - 1;

      loading.value = numCallsPending !== 0;

      loadedHook.trigger(loadingFirstTime.value ? 'loadedFirstTime' : 'loaded');

      loadingFirstTime.value = false;

      isFirstTime = false;
    });

    return result;
  };

  async function actionWithoutLoading(
    ...args: Parameters<typeof call>
  ): Promise<Awaited<ReturnType<typeof call>>> {
    return await call(...args);
  }

  return {
    action,
    actionWithoutLoading,
    loading: readonly(loading),
    loadingFirstTime: readonly(loadingFirstTime),
    onLoaded: loadedHook.on,
  };
}
